import type { JSX } from 'react'
import type { MatchStatus } from '@/types/match'

interface Props {
  status: MatchStatus
  isTiebreak?: boolean
  currentSet?: number
  className?: string
}

function IconScheduled() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  )
}
function IconWarmup() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    </svg>
  )
}
function IconLive() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21"/>
    </svg>
  )
}
function IconPause() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
    </svg>
  )
}
function IconTimeout() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><polyline points="12,6 12,12 16,14"/>
    </svg>
  )
}
function IconFinished() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"/>
    </svg>
  )
}
function IconPostponed() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  )
}

const STATUS_CONFIG: Record<MatchStatus, {
  label: string
  color: string
  pulse?: boolean
  Icon: () => JSX.Element
}> = {
  scheduled:     { label: 'Programmata',    color: '#676593', Icon: IconScheduled },
  warmup:        { label: 'Riscaldamento',  color: '#FE7200', pulse: true,  Icon: IconWarmup },
  live:          { label: 'In corso',       color: '#ef4444', pulse: true,  Icon: IconLive },
  pause:         { label: 'Pausa',          color: '#FE7200', Icon: IconPause },
  timeout_home:  { label: 'Timeout',        color: '#FE7200', pulse: true,  Icon: IconTimeout },
  timeout_away:  { label: 'Timeout',        color: '#FE7200', pulse: true,  Icon: IconTimeout },
  finished:      { label: 'Conclusa',       color: '#676593', Icon: IconFinished },
  postponed:     { label: 'Rinviata',       color: '#676593', Icon: IconPostponed },
}

export default function MatchStatus({ status, isTiebreak, currentSet, className = '' }: Props) {
  const cfg = STATUS_CONFIG[status]
  const { Icon } = cfg

  const setLabel = currentSet
    ? isTiebreak ? 'Tiebreak' : `Set ${currentSet}`
    : null

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {cfg.pulse ? (
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: cfg.color }} />
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: cfg.color }} />
        </span>
      ) : (
        <span style={{ color: cfg.color }} className="flex-shrink-0">
          <Icon />
        </span>
      )}
      <span
        className="font-display font-bold uppercase tracking-widest text-[10px]"
        style={{ color: cfg.color }}
      >
        {cfg.label}
      </span>
      {setLabel && status === 'live' && (
        <>
          <span className="text-white/30 text-[10px]">·</span>
          <span className="font-display font-bold uppercase tracking-widest text-[10px] text-white/60">
            {setLabel}
          </span>
        </>
      )}
    </div>
  )
}
